import FunnelLayout from "@components/quiz/FunnelLayout"
import QuizHeader from "@components/quiz/QuizHeader"
import { setQuizAnswer } from "@helpers/quizAnswers"
import { useCallback } from "react"
import { useTranslation } from "react-i18next"
import {
  Navigate,
  useLocation,
  useNavigate,
  useNavigationType,
  useParams,
} from "react-router-dom"
import {
  getStepIndex,
  ONBOARDING_BASE_PATH,
  QUIZ_STEPS,
  QuizStep,
} from "./quizSteps"
import DailyGoalStep from "./steps/DailyGoalStep"
import InfoStep from "./steps/InfoStep"
import LikertStep from "./steps/LikertStep"
import LoaderStep from "./steps/LoaderStep"
import MultiSelectStep from "./steps/MultiSelectStep"
import SingleSelectStep from "./steps/SingleSelectStep"
import StatementStep from "./steps/StatementStep"
import trackQuizAnswer from "./trackQuizAnswer"

function getSectionProgress(step: QuizStep): number {
  const sectionSteps = QUIZ_STEPS.filter(
    (quizStep) => quizStep.section === step.section,
  )

  return (sectionSteps.indexOf(step) + 0.5) / sectionSteps.length
}

function getQuestionKey(step: QuizStep): string {
  switch (step.type) {
    case "single":
    case "multi":
    case "info":
    case "dailyGoal":
      return `pages.onboarding.steps.${step.id}.heading`
    case "statement":
    case "likert":
      return `pages.onboarding.steps.${step.id}.statement`
    case "loader":
      return "pages.onboarding.loader.heading"
    default: {
      const exhaustiveCheck: never = step
      throw new Error(`Unhandled step: ${String(exhaustiveCheck)}`)
    }
  }
}

export default function QuizStepPage() {
  const { stepId = "" } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const navigationType = useNavigationType()
  const { t } = useTranslation()

  const stepIndex = getStepIndex(stepId)
  const step = QUIZ_STEPS[stepIndex]

  const goNext = useCallback(() => {
    const nextStep = QUIZ_STEPS[stepIndex + 1]
    const nextPath = nextStep
      ? `${ONBOARDING_BASE_PATH}/${nextStep.id}`
      : `${ONBOARDING_BASE_PATH}/email`

    navigate(nextPath + location.search)
  }, [location.search, navigate, stepIndex])

  const handleAnswer = useCallback(
    (answers: string[]) => {
      setQuizAnswer(step.id, answers)
      trackQuizAnswer({
        answers,
        question: t(getQuestionKey(step), { lng: "en" }),
        stepIndex,
      })
      goNext()
    },
    [goNext, step, stepIndex, t],
  )

  const handlePopupAnswer = useCallback(
    (question: string, answer: string) =>
      trackQuizAnswer({ answers: [answer], question, stepIndex }),
    [stepIndex],
  )

  if (!step)
    return <Navigate replace to={ONBOARDING_BASE_PATH + location.search} />

  const renderStep = () => {
    switch (step.type) {
      case "single":
        return (
          <SingleSelectStep
            id={step.id}
            onAnswer={handleAnswer}
            options={step.options}
          />
        )
      case "multi":
        return (
          <MultiSelectStep
            id={step.id}
            layout={step.layout}
            onAnswer={handleAnswer}
            options={step.options}
          />
        )
      case "statement":
        return <StatementStep id={step.id} onAnswer={handleAnswer} />
      case "likert":
        return <LikertStep id={step.id} onAnswer={handleAnswer} />
      case "dailyGoal":
        return <DailyGoalStep id={step.id} onAnswer={handleAnswer} />
      case "info":
        return (
          <InfoStep id={step.id} onContinue={goNext} visual={step.visual} />
        )
      case "loader":
        return (
          <LoaderStep onComplete={goNext} onPopupAnswer={handlePopupAnswer} />
        )
      default: {
        const exhaustiveCheck: never = step
        throw new Error(`Unhandled step: ${String(exhaustiveCheck)}`)
      }
    }
  }

  const enterAnimation =
    navigationType === "POP"
      ? "animate-slide-in-left"
      : "animate-slide-in-right"

  return (
    <FunnelLayout
      header={
        <QuizHeader
          onBack={step.type === "loader" ? undefined : () => navigate(-1)}
          section={step.section}
          sectionProgress={getSectionProgress(step)}
        />
      }
    >
      <div className={`flex grow flex-col ${enterAnimation}`} key={step.id}>
        {renderStep()}
      </div>
    </FunnelLayout>
  )
}
